import React, { useState } from 'react';
import { X, MapPin, Phone, Globe, Tag, Package, Share2, Copy, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { toast } from 'sonner';

export const ActivityModal = ({ activity, open, onClose }) => {
  const [copied, setCopied] = useState(false);
  // Stato per gestire quale immagine visualizzare come "principale" nel modale
  // Se null, mostra la cover originale. Altrimenti l'URL selezionato dalla gallery.
  const [selectedImage, setSelectedImage] = useState(null);

  if (!activity) return null;

  // Uniamo cover e gallery per creare una lista unica di immagini navigabili
  const allImages = [activity.image, ...(activity.gallery || [])].filter(Boolean);
  const currentDisplayImage = selectedImage || activity.image;

  // Generate shareable URL and text
  const shareUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const shareText = `Scopri ${activity.name} su VeCo Shop! ${activity.discount} 🎉`;
  const fullShareText = `${shareText}\n\n${activity.description}\n\n📍 ${activity.address}\n📞 ${activity.phone}\n\nVisita: ${shareUrl}`;

  // Share handlers
  const shareViaWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(fullShareText)}`;
    window.open(url, '_blank');
  };

  const shareViaEmail = () => {
    const subject = encodeURIComponent(`${activity.name} - VeCo Shop`);
    const body = encodeURIComponent(fullShareText);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const shareViaFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const shareViaTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const shareViaLinkedIn = () => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copiato negli appunti!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Impossibile copiare il link');
    }
  };

  const shareViaNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: activity.name,
          text: shareText,
          url: shareUrl,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error sharing:', err);
        }
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={(val) => {
        if (!val) setSelectedImage(null); // Reset immagine quando chiude
        onClose();
    }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-gray-900">
            {activity.name}
          </DialogTitle>
          <DialogDescription className="text-gray-500 text-sm mt-1">
            {activity.category} {activity.zone ? `- ${activity.zone}` : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Main Image View */}
          <div className="relative h-64 md:h-96 rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
            <img
              src={currentDisplayImage}
              alt={activity.name}
              className="w-full h-full object-cover transition-all duration-300"
            />
            {activity.featured && (
              <Badge className="absolute top-4 right-4 bg-green-500 hover:bg-green-600 border-0">
                In Evidenza
              </Badge>
            )}
          </div>

          {/* Gallery Thumbnails (Solo se ci sono più immagini) */}
          {allImages.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                  {allImages.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setSelectedImage(img)}
                        className={`relative w-20 h-20 flex-shrink-0 rounded-md overflow-hidden border-2 transition-all ${
                            currentDisplayImage === img ? 'border-teal-600 ring-2 ring-teal-100' : 'border-transparent opacity-70 hover:opacity-100'
                        }`}
                      >
                          <img src={img} alt={`Thumb ${idx}`} className="w-full h-full object-cover" />
                      </button>
                  ))}
              </div>
          )}

          {/* Category */}
          <div>
            <Badge variant="outline" className="text-teal-700 border-teal-300">
              {activity.category}
            </Badge>
          </div>

          {/* Discount Highlight */}
          <div className="bg-gradient-to-r from-teal-500 to-purple-600 text-white rounded-xl p-6">
            <div className="flex items-center">
              <Tag className="w-6 h-6 mr-3" />
              <div>
                <p className="text-sm font-medium opacity-90">Sconto Riservato</p>
                <p className="text-2xl font-bold">{activity.discount}</p>
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Descrizione</h3>
            <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{activity.description}</p>
          </div>

          {/* Products/Services */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <Package className="w-5 h-5 mr-2 text-teal-600" />
              Prodotti e Servizi con Sconto
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {activity.products && activity.products.map((product, index) => (
                <div
                  key={index}
                  className="flex items-center bg-gray-50 rounded-lg p-3 border border-gray-200"
                >
                  <div className="w-2 h-2 rounded-full bg-teal-500 mr-3" />
                  <span className="text-sm text-gray-700">{product}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Info */}
          <div className="border-t pt-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Informazioni di Contatto</h3>
            
            <div className="flex items-start">
              <MapPin className="w-5 h-5 mr-3 mt-0.5 text-purple-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-700">Indirizzo</p>
                <p className="text-gray-600">{activity.address}</p>
              </div>
            </div>

            <div className="flex items-start">
              <Phone className="w-5 h-5 mr-3 mt-0.5 text-green-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-700">Telefono</p>
                <a href={`tel:${activity.phone}`} className="text-teal-600 hover:underline">
                  {activity.phone}
                </a>
              </div>
            </div>

            {activity.website && (
              <div className="flex items-start">
                <Globe className="w-5 h-5 mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-700">Sito Web</p>
                  <a
                    href={`https://${activity.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-teal-600 hover:underline"
                  >
                    {activity.website}
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* CTA */}
          <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
            <p className="text-sm text-gray-700">
              <span className="font-semibold text-teal-700">Ricorda:</span> Per usufruire dello sconto,
              presenta la tua <span className="font-semibold">Tessera VeCo</span> prima del pagamento.
            </p>
          </div>

          {/* Share Section */}
          <div className="border-t pt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Share2 className="w-5 h-5 mr-2 text-purple-600" />
                Condividi questa attività
              </h3>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {/* WhatsApp */}
              <Button
                onClick={shareViaWhatsApp}
                className="bg-[#25D366] hover:bg-[#20BA5A] text-white"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                WhatsApp
              </Button>

              {/* Email */}
              <Button
                onClick={shareViaEmail}
                className="bg-gray-600 hover:bg-gray-700 text-white"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Email
              </Button>

              {/* Facebook */}
              <Button
                onClick={shareViaFacebook}
                className="bg-[#1877F2] hover:bg-[#166FE5] text-white"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                Facebook
              </Button>

              {/* Copy Link */}
              <Button
                onClick={copyLink}
                variant="outline"
                className="border-gray-300 hover:bg-gray-50"
              >
                {copied ? (
                  <>
                    <Check className="w-5 h-5 mr-2 text-green-600" />
                    <span className="text-green-600">Copiato!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-5 h-5 mr-2" />
                    Copia Link
                  </>
                )}
              </Button>
            </div>

            {/* Additional share options */}
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                onClick={shareViaTwitter}
                size="sm"
                variant="outline"
                className="border-[#1DA1F2] text-[#1DA1F2] hover:bg-[#1DA1F2] hover:text-white"
              >
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                </svg>
                Twitter
              </Button>

              <Button
                onClick={shareViaLinkedIn}
                size="sm"
                variant="outline"
                className="border-[#0A66C2] text-[#0A66C2] hover:bg-[#0A66C2] hover:text-white"
              >
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                LinkedIn
              </Button>

              {/* Native Share (mobile) */}
              {typeof navigator !== 'undefined' && navigator.share && (
                <Button
                  onClick={shareViaNative}
                  size="sm"
                  variant="outline"
                  className="border-purple-300 text-purple-700 hover:bg-purple-50"
                >
                  <Share2 className="w-4 h-4 mr-1" />
                  Altro...
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};